
import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

export const Type = {
    Phone: 'phone',
    Email: 'email',
    Web: 'web'
};

export type TypeType = typeof Type[keyof typeof Type];
 /**
 * Resource
 *
 * A contact method — phone number, email address, or website URL — belonging to a contact or other entity. Each record stores the type, a categorizing label (e.g. mobile, home, business), the actual value, and flags for primary, current, and do-not-contact status. Changes are propagated to the payment processor after save.
 */
class Resource extends Base implements EntityType {
    resourceId: uuid;
    type: TypeType;
    label: string;
    value: string;
    canContact: boolean;
    isPrimary: boolean;
    isCurrent: boolean;
    notes?: string | null;

    /**
     * Create a new Resource, with an (optional) JSON object or Resource class
     *
     * @param {Object|Resource} doc - Can be either a simple JSON object or a Resource class used to initialize this object's fields
     * @constructor
     */
    constructor(doc: typeof Resource | any) {
        super(doc);
        
        // @ts-ignore
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: 'C',
            entityId: null,
            entityType: null,
            resourceId: genUUID(),
            type: null,
            label: null,
            value: null,
        };
        
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || 'resource';
        // @ts-ignore
        this.resourceId = doc._resourceId || doc.resourceId || doc.resourceid || genUUID();
        // @ts-ignore
        this.type = doc._type || doc.type || null;
        // @ts-ignore
        this.label = doc._label || doc.label || null;
        // @ts-ignore
        this.value = doc._value || doc.value || null;
        // @ts-ignore
        this.canContact = util.defaultBoolean([doc.canContact, doc._canContact, doc.cancontact, true]);
        // @ts-ignore
        this.isPrimary = util.defaultBoolean([doc.isPrimary, doc._isPrimary, doc.isprimary, false]);
        // @ts-ignore
        this.isCurrent = util.defaultBoolean([doc.isCurrent, doc._isCurrent, doc.iscurrent, true]);
        // @ts-ignore
        this.notes = doc._notes || doc.notes || null;

    }
    /**
     * @returns {uuid} This is a generic pointer to the resourceId across all entity types
     */
    get id(): uuid {
        return this.resourceId;
    }


    static TYPE_VALUES = ['phone', 'email', 'web'];
    
    /**
     * Get a list of pk fields (primary keys)
     *
     * @returns {SchemaField[]}
     */
    static override getPkFields(): SchemaField[] {
        return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique ID for this resource"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}];
    }
    /**
     * TODO: Added just to handle react-select issues.  Can remove when remedied.
     */
    toLowerCase() {
        return this.resourceId;
    }
    
    /**
     * How should this item be displayed in a list?
     */
    displayFn() {
        return `${this.label} ${this.type}: ${this.value}`;
    }
    
    /**
     * Get a list of non-reference array fields in the schema
     *
     * @return {Array.<{field: string, entity: string}>}
     */
    static override getNonRefArrayFields(): string[] {
        return [];
    }

    /**
     * Default entries are Outlaw-supplied Resource entries
     * @static
     * @return {object[]}
     */
    static override getDefaultEntries(): Resource[] {
        return [];
    }

    /**
     * Get a reference to the schema entity.
     * @static
     * @return {object} - the original schema
     */
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
            entity: 'resource',
            table: 'RESOURCE',
            comment: 'A contact method — phone number, email address, or website URL — belonging to a contact or other entity. Each record stores the type, a categorizing label (e.g. mobile, home, business), the actual value, and flags for primary, current, and do-not-contact status. Changes are propagated to the payment processor after save.',
            fields: {
                resourceId: {
                    type: 'uuid',
                    required: true,
                    pk: true,
                    default: '__UUID__',
                    comment: 'Unique ID for this resource'
                },
                type: {
                    type: 'string',
                    required: true,
                    values: ['phone', 'email', 'web'],
                    comment: 'The type of resource: phone, email, or web'
                },
                label: {
                    type: 'string',
                    required: true,
                    comment: 'A non-unique label for the resource, e.g. Home'
                },
                value: {
                    type: 'string',
                    required: true,
                    comment: 'The value of the resource, e.g. an email or phone number'
                },
                canContact: {
                    type: 'boolean',
                    default: true,
                    comment: 'Can the person/entity be contacted with this information?'
                },
                isPrimary: {
                    type: 'boolean',
                    default: false,
                    comment: 'Is this the primary resource (of this type)?'
                },
                isCurrent: {
                    type: 'boolean',
                    default: true,
                    comment: 'Is this resource current?'
                },
                notes: {
                    type: 'string',
                    required: false,
                    comment: 'Notes on this resource, to help differentiate'
                }
            },
            displayFn: "`${this.label} ${this.type}: ${this.value}`",
            indexes: [{
                name: 'notDeleted',
                fn: t => t.status !== 'D'
            }],
            maps: [{
                name: 'entityId',
                keysFn: t => t.entityId,
                presentFn: (t, k) => t.entityId === k && t.status !== 'D'
            }],
            hooks: [{
                when: 'after',
                appliesTo: 'save',
                method: 'updatePaymentProcessor',
                async: true
            }]
        });
    }

}
export default Resource;
